import {HttpErrorResponse} from '@angular/common/http';
import {Injectable, NgZone} from '@angular/core';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AnalyticsService} from '@npck/libs/analytics-sdk';
import {BusinessError, BusinessErrorCode, CommonErrorHandler, extractRequestId, RouterService} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {AuthHolderService} from '../domain/auth-holder.service';
import {OauthService} from '../domain/oauth.service';
import {AUTH_ERROR, AUTH_SESSION_EXPIRED} from './analytics-events';

@Injectable()
export class AppErrorHandler extends CommonErrorHandler {
    constructor(
        zone: NgZone,
        routerService: RouterService,
        private authHolderService: AuthHolderService,
        private oauthService: OauthService,
        private analyticsService: AnalyticsService,
        private dialogService: DialogService,
    ) {
        super(zone, routerService);
    }

    hideAllUiElements(): void {
        super.hideAllUiElements();
        this.dialogService.closeAllDialogs();
    }

    handleAccessError(error: HttpErrorResponse): void {
        this.analyticsService.capture(AUTH_SESSION_EXPIRED);
        this.handleOauthIfInProgress(error, OauthRedirectErrorCode.unauthorized, error.error?.errorDescription);
    }

    handleBusinessError(error: BusinessError): void {
        this.handleOauthIfInProgress(error, this.convertBusinessErrorToOauthRedirect(error));
    }

    handleServerError(error: any): void {
        this.handleOauthIfInProgress(error, OauthRedirectErrorCode.server_error);
    }

    handleNetworkError(error: any): void {
        this.oauthService.handleOauthError(Error.NETWORK_UNAVAILABLE, extractRequestId(error));
    }

    private handleOauthIfInProgress(error: any, oauthRedirectErrorCode: OauthRedirectErrorCode, errorDescription?: string): void {
        if (this.authHolderService.hasAuthSession()) {
            this.oauthService.handleOauthErrorAndRedirect(oauthRedirectErrorCode, extractRequestId(error), errorDescription);
        } else {
            this.analyticsService.capture(AUTH_ERROR, {reason: oauthRedirectErrorCode, message: errorDescription});
            super.handleServerError(error);
        }
    }

    private convertBusinessErrorToOauthRedirect(error: BusinessError): OauthRedirectErrorCode {
        switch (error.error) {
            case BusinessErrorCode.ESIGN_CERTEX_CERTIFICATE_AUTHORITY_ERROR:
            case BusinessErrorCode.ESIGN_CERTEX_UNAVAILABLE:
                return OauthRedirectErrorCode.esign_error;
            default :
                return Object.values(OauthRedirectErrorCode).find(it => it === error.error.toLowerCase()) ||
                    OauthRedirectErrorCode.server_error;
        }
    }
}

export enum Error {
    PAGE_NOT_FOUND = 'PAGE_NOT_FOUND',
    SERVER_ERROR = 'SERVER_ERROR',
    INVALID_LINK = 'INVALID_LINK',
    SESSION_COMPLETED = 'SESSION_COMPLETED',
    NETWORK_UNAVAILABLE = 'NETWORK_UNAVAILABLE',
    CAMERA_UNAVAILABLE = 'CAMERA_UNAVAILABLE',
    DEVICE_NOT_SUPPORTED = 'DEVICE_NOT_SUPPORTED',
}
